import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, Bot, Server, FolderGit2, MessageSquare, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    agents_count: 0,
    projects_count: 0,
    ssh_connections_count: 0,
    messages_count: 0,
    recent_activity: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Ошибка загрузки статистики');
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Активные агенты',
      value: stats.agents_count,
      icon: Bot,
      color: 'from-violet-500 to-purple-600',
      description: 'Работают в системе'
    },
    {
      title: 'SSH подключения',
      value: stats.ssh_connections_count,
      icon: Server,
      color: 'from-blue-500 to-cyan-600',
      description: 'Настроенные сервера'
    },
    {
      title: 'Проекты',
      value: stats.projects_count,
      icon: FolderGit2,
      color: 'from-emerald-500 to-teal-600',
      description: 'Активные проекты'
    },
    {
      title: 'Сообщения',
      value: stats.messages_count,
      icon: MessageSquare,
      color: 'from-pink-500 to-rose-600',
      description: 'Всего запросов'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">
          Добро пожаловать в <span className="text-gradient">AI Agents System</span>
        </h1>
        <p className="text-gray-400 text-lg">
          Управление серверами и разработка с помощью ИИ
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Card key={index} className="glass-panel border-white/10 bg-white/5" data-testid={`stat-card-${index}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">
                  {card.title}
                </CardTitle>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white mb-1">
                  {card.value}
                </div>
                <p className="text-xs text-gray-400">
                  {card.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Start Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Getting Started */}
        <Card className="glass-panel border-white/10 bg-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-violet-400" />
              Быстрый старт
            </CardTitle>
            <CardDescription className="text-gray-400">
              Начните работу с системой
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-violet-500 text-white text-sm flex items-center justify-center">1</span>
                Настройте SSH подключения
              </h3>
              <p className="text-sm text-gray-400">
                Добавьте свои сервера для управления
              </p>
            </div>
            
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-sm flex items-center justify-center">2</span>
                Создайте проект
              </h3>
              <p className="text-sm text-gray-400">
                Организуйте свои сайты и сервисы
              </p>
            </div>
            
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-sm flex items-center justify-center">3</span>
                Начните общаться с AI
              </h3>
              <p className="text-sm text-gray-400">
                Используйте чат для управления и разработки
              </p>
            </div>
          </CardContent>
        </Card>

        {/* System Info */}
        <Card className="glass-panel border-white/10 bg-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Возможности системы
            </CardTitle>
            <CardDescription className="text-gray-400">
              Что могут ваши AI агенты
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-violet-500 mt-2"></div>
              <div>
                <h4 className="text-white font-medium">Разработка кода</h4>
                <p className="text-sm text-gray-400">React, FastAPI, Python, JavaScript</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2"></div>
              <div>
                <h4 className="text-white font-medium">SSH управление</h4>
                <p className="text-sm text-gray-400">Выполнение команд на серверах</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2"></div>
              <div>
                <h4 className="text-white font-medium">Мониторинг и диагностика</h4>
                <p className="text-sm text-gray-400">Анализ логов и ошибок</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-pink-500 mt-2"></div>
              <div>
                <h4 className="text-white font-medium">Деплоймент</h4>
                <p className="text-sm text-gray-400">Автоматическое развертывание</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2"></div>
              <div>
                <h4 className="text-white font-medium">Управление БД</h4>
                <p className="text-sm text-gray-400">MongoDB, PostgreSQL, MySQL</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-cyan-500 mt-2"></div>
              <div>
                <h4 className="text-white font-medium">FastPanel интеграция</h4>
                <p className="text-sm text-gray-400">Автоматическая установка и настройка</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;